import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';

import {
	EXTENSION_KEY,
	EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';

export const INITIAL_MAX_LCM_SSR_NESTING_LEVEL = 0;

const MAX_NODES_TO_ANALYZE = 100_000;
const EXTENSION_NODE_TYPES = new Set([
	'extension',
	'inlineExtension',
	'bodiedExtension',
	'multiBodiedExtension',
]);

type AdfNode = {
	attrs?: {
		parameters?: {
			nestedContent?: unknown;
		};
	};
	content?: unknown;
	type?: unknown;
};

type LcmExtensionParameters = Parameters & {
	nestedContent?: unknown;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const appendNodes = (nodes: unknown[], children: unknown[]): void => {
	for (const child of children) {
		nodes.push(child);
	}
};

/**
 * The pre-telemetry SSR policy is deliberately all-or-nothing at level 0. Missing nested content
 * uses the renderer's existing default empty document; malformed, cyclic, excessively large, or
 * extension-containing documents fail closed.
 */
export function isInitialLcmSsrEligible(
	extension: ExtensionParams<LcmExtensionParameters>,
): boolean {
	if (
		extension.extensionType !== EXTENSION_TYPE.MIGRATION ||
		extension.extensionKey !== EXTENSION_KEY.LEGACY_CONTENT
	) {
		return false;
	}

	const nestedContent = extension.parameters?.nestedContent;
	if (nestedContent == null) {
		return true;
	}
	if (
		!isObject(nestedContent) ||
		nestedContent.type !== 'doc' ||
		!Array.isArray(nestedContent.content)
	) {
		return false;
	}

	const nodes: unknown[] = [nestedContent];
	const visited = new WeakSet<object>();
	let visitedNodeCount = 0;

	while (nodes.length > 0) {
		const current = nodes.pop();
		if (!isObject(current) || visited.has(current)) {
			return false;
		}
		visited.add(current);

		visitedNodeCount++;
		if (visitedNodeCount > MAX_NODES_TO_ANALYZE) {
			return false;
		}

		const node = current as AdfNode;
		if (typeof node.type === 'string' && EXTENSION_NODE_TYPES.has(node.type)) {
			return false;
		}

		if (node.content !== undefined) {
			if (!Array.isArray(node.content)) {
				return false;
			}
			if (visitedNodeCount + nodes.length + node.content.length > MAX_NODES_TO_ANALYZE) {
				return false;
			}
			appendNodes(nodes, node.content);
		}

		const nestedDocument = node.attrs?.parameters?.nestedContent;
		if (nestedDocument !== undefined) {
			if (
				!isObject(nestedDocument) ||
				nestedDocument.type !== 'doc' ||
				!Array.isArray(nestedDocument.content)
			) {
				return false;
			}
			nodes.push(nestedDocument);
		}
	}

	return true;
}
